import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../../../components/Toast';
import { getImageUrl } from '../../../utils/mediaUtils';
import { 
  Megaphone, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  Calendar, 
  Eye, 
  X, 
  FileText, 
  Edit3, 
  User, 
  CheckCircle2, 
  ListPlus,
  Layers,
  Image as ImageIcon
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const FEATURE_ICON_OPTIONS = [
  { value: 'fa-handshake', label: 'Jabat Tangan (Kemitraan / Partnership)' },
  { value: 'fa-building', label: 'Gedung / Korporat (Corporate / Industri)' },
  { value: 'fa-university', label: 'Universitas (Kerjasama Akademik)' },
  { value: 'fa-graduation-cap', label: 'Topi Wisuda (Program Pendidikan)' },
  { value: 'fa-microscope', label: 'Mikroskop (Riset & Inovasi STEM)' },
  { value: 'fa-heart', label: 'Hati (Kepedulian / Community Outreach)' },
  { value: 'fa-users', label: 'Komunitas (Team / Siswa & Staf)' },
  { value: 'fa-globe', label: 'Globe (Wawasan Global)' },
  { value: 'fa-award', label: 'Medali (Award / Penghargaan)' },
  { value: 'fa-star', label: 'Bintang (Star / Sorotan Utama)' },
  { value: 'fa-trophy', label: 'Piala (Trophy / Prestasi)' },
  { value: 'fa-book-open', label: 'Buku (Kurikulum & Pembelajaran)' }
];

export default function NewsPressForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const [formData, setFormData] = useState({
    kicker: 'Press Releases',
    title: 'Community Involvement & Partnerships',
    description: 'Official updates on our outreach programs, strategic collaborations, and community initiatives that strengthen our school and local community.',
    items: [
      {
        id: 'community-involvement',
        title: 'Community Involvement',
        subtitle: 'Building Stronger Communities Together',
        category: 'Press Release',
        author: 'Communications Team',
        description: 'Highlights of outreach programs, service learning, and local collaborations led by students and staff.',
        content: 'Permata Harapan Secondary is proud to announce its continued dedication to community service and active engagement. Through student-led initiatives, our school has partnered with local non-profit organizations to deliver educational workshops, environmental clean-ups, and donation drives.',
        highlights: [
          'Over 500 students participated in community service this year',
          'Partnerships with 15+ local organizations and NGOs',
          'Monthly community outreach events and initiatives'
        ],
        features: [
          {
            icon: 'fa-users',
            title: 'Student Leadership',
            detail: 'Students lead community service projects and mentor younger peers in outreach initiatives.'
          },
          {
            icon: 'fa-heart',
            title: 'Local Partnerships',
            detail: 'Collaborations with local organizations to address community needs and create meaningful impact.'
          }
        ],
        image: '/assets/news/news4.jpg',
        images: ['/assets/news/news4.jpg', '', ''],
        date: 'October 2026',
        link: 'news-press-community-involvement'
      },
      {
        id: 'partnerships',
        title: 'Strategic Partnerships',
        subtitle: 'Collaborations That Strengthen Learning & Student Opportunities',
        category: 'Press Release',
        author: 'Administration',
        description: 'Updates on strategic partnerships that enhance our educational programs, facilities, and student opportunities.',
        content: 'We have forged strategic collaborations with leading institutions and industry leaders to enrich our students academic and extracurricular pathways. These partnerships offer internships, guest lectures, and shared research resources that prepare students for higher education and future careers.',
        highlights: [
          'Partnership with leading universities for advanced programs',
          'Internship opportunities with corporate partners',
          'Joint research projects in STEM and innovation'
        ],
        features: [
          {
            icon: 'fa-university',
            title: 'University Partnerships',
            detail: 'Collaborations with leading universities for advanced learning programs and research opportunities.'
          },
          {
            icon: 'fa-building',
            title: 'Corporate Partnerships',
            detail: 'Industry partnerships providing internships, mentorship, and career development opportunities.'
          }
        ],
        image: '/assets/news/news7.jpg',
        images: ['/assets/news/news7.jpg', '', ''],
        date: 'September 2026',
        link: 'news-press-partnerships'
      }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });
  const [pendingItems, setPendingItems] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  // Full Detail Modal State
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // null = new, number = edit
  const [editingItem, setEditingItem] = useState({
    id: '',
    title: '',
    subtitle: '',
    category: 'Press Release',
    author: 'Administration',
    date: '',
    description: '',
    content: '',
    highlights: [],
    features: [],
    image: '',
    images: ['', '', ''],
    link: ''
  });

  // Modal sub-form states
  const [newHighlightText, setNewHighlightText] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState('fa-handshake');
  const [newFeatureTitle, setNewFeatureTitle] = useState('');
  const [newFeatureDetail, setNewFeatureDetail] = useState('');
  const [slideUploading, setSlideUploading] = useState({ 0: false, 1: false, 2: false });
  const [slideUploadProgress, setSlideUploadProgress] = useState({ 0: 0, 1: 0, 2: 0 });

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!res.ok) return;
      const json = await res.json();
      if (json.ok && Array.isArray(json.data)) {
        const map = {};
        json.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('[NewsPressForm] Media fetch error:', err.message);
    }
  };

  const resolveImage = (mediaId, index = 0) => {
    if (!mediaId || brokenImages[index]) return '';
    if (mediaMap && (mediaMap[mediaId] || mediaMap[String(mediaId)])) {
      const mapped = mediaMap[mediaId] || mediaMap[String(mediaId)];
      if (mapped.startsWith('http://') || mapped.startsWith('https://')) return mapped;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${mapped}` : mapped;
    }
    const resolved = getImageUrl(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) return resolved;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${resolved}` : resolved;
    }
    return '';
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=news-updates`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'news-press' || 
          s.section_type === 'news-press-releases' || 
          s.section_type === 'press'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=news-updates`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'news-press' || 
            s.section_type === 'news-press-releases' || 
            s.section_type === 'press'
          );
        }
      }

      // If section is still not found, auto-ensure it exists via create-or-get endpoint
      if (!foundSection) {
        try {
          const createRes = await fetch(`${apiBase}/homepage/cms/sections/create-or-get`, {
            method: 'POST',
            credentials: 'include',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              page: 'news-updates',
              section_type: 'news-press',
              content: {
                en: {
                  kicker: 'Official Announcements',
                  title: 'Press Releases & Community Notices',
                  description: 'Read formal statements, strategic announcements, and verified information issued by Permata Harapan Secondary.',
                  items: formData.items
                }
              }
            })
          });
          if (createRes.ok) {
            const createJson = await createRes.json();
            foundSection = createJson.data;
          }
        } catch (ce) {
          console.warn('[NewsPressForm] Auto-create section error:', ce);
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const rawItems = Array.isArray(en.items) && en.items.length > 0 
          ? en.items 
          : (Array.isArray(en.press_releases) && en.press_releases.length > 0 ? en.press_releases : formData.items);

        const loadedItems = rawItems.map((item, idx) => ({
          id: item.id || `press-${idx + 1}`,
          title: item.title || '',
          subtitle: item.subtitle || '',
          category: item.category || 'Press Release',
          author: item.author || 'Administration',
          description: item.description || '',
          content: item.content || item.body || item.full_content || item.description || '',
          highlights: Array.isArray(item.highlights) ? item.highlights : (typeof item.highlights === 'string' ? [item.highlights] : []),
          features: Array.isArray(item.features) ? item.features : [],
          image: item.image || (Array.isArray(item.images) ? item.images[0] : '') || '',
          images: Array.isArray(item.images) && item.images.length > 0 
            ? item.images 
            : (item.image ? [item.image] : []),
          date: item.date || '',
          link: item.link || (item.id ? `news-press-${item.id}` : '')
        }));

        const loadedData = {
          kicker: en.kicker || 'Press Releases',
          title: en.title || 'Community Involvement & Partnerships',
          description: en.description || 'Official updates on our outreach programs, strategic collaborations, and community initiatives that strengthen our school and local community.',
          items: loadedItems
        };

        const pFields = { kicker: false, title: false, description: false };
        const pItems = {};

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  let val = c.new_value;
                  if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                    try { val = JSON.parse(val); } catch (e) {}
                  }

                  if (['kicker', 'title', 'description'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                    if (val !== undefined && val !== null) {
                      loadedData[c.field_id] = val;
                    }
                  } else if (c.field_id?.includes('items.')) {
                    const m = c.field_id.match(/items\.(\d+)(?:\.(add|update|delete))?/);
                    if (m) {
                      const itemIdx = parseInt(m[1], 10);
                      const op = m[2] || 'update';
                      pItems[`item_${itemIdx}`] = true;
                      if (op === 'delete') {
                        pItems[`item_${itemIdx}_delete`] = true;
                      } else if (val && typeof val === 'object') {
                        if (loadedItems[itemIdx]) {
                          loadedItems[itemIdx] = { ...loadedItems[itemIdx], ...val };
                        } else {
                          loadedItems.push(val);
                        }
                      }
                    }
                  } else if (c.field_id === 'items') {
                    pFields.items = true;
                  }
                }
              });
            }
          }
        } catch (pe) {
          console.warn('[NewsPressForm] Pending check error:', pe);
        }

        setFormData(loadedData);
        setOriginalData(loadedData);
        setPendingFields(pFields);
        setPendingItems(pItems);
      }
    } catch (err) {
      console.error('[NewsPressForm] Load error:', err);
      toast.error('Failed to load Press Releases content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const next = [...prev.items];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, items: next };
    });
  };

  // Open Modal for adding new press release
  const handleOpenAddModal = () => {
    setEditingIndex(null);
    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setNewFeatureIcon('fa-handshake');
    setSlideUploading({ 0: false, 1: false, 2: false });
    setSlideUploadProgress({ 0: 0, 1: 0, 2: 0 });

    setEditingItem({
      id: `press-${Date.now()}`,
      title: '',
      subtitle: '',
      category: 'Press Release',
      author: 'Administration',
      date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      description: '',
      content: '',
      highlights: [],
      features: [],
      image: '',
      images: ['', '', ''],
      link: ''
    });
    setEditorModalOpen(true);
  };

  // Open Modal for editing existing press release
  const handleOpenEditModal = (index) => {
    setEditingIndex(index);
    const item = formData.items[index];
    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setNewFeatureIcon('fa-handshake');
    setSlideUploading({ 0: false, 1: false, 2: false });
    setSlideUploadProgress({ 0: 0, 1: 0, 2: 0 });

    const existingImages = Array.isArray(item.images) && item.images.length > 0 
      ? item.images 
      : (item.image ? [item.image] : []);
    const images3 = [
      resolveImage(existingImages[0]) || existingImages[0] || item.image || '',
      resolveImage(existingImages[1]) || existingImages[1] || '',
      resolveImage(existingImages[2]) || existingImages[2] || ''
    ];

    setEditingItem({
      ...item,
      author: item.author || 'Administration',
      subtitle: item.subtitle || '',
      highlights: Array.isArray(item.highlights) ? [...item.highlights] : [],
      features: Array.isArray(item.features) ? [...item.features] : [],
      images: images3,
      image: images3[0] || item.image || ''
    });
    setEditorModalOpen(true);
  };

  // Upload image for specific slide (0, 1, or 2) in modal with real-time percentage
  const handleModalSlideImageUpload = (slideIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran file maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format gambar harus JPG, PNG, atau WebP');
      return;
    }

    setSlideUploading(prev => ({ ...prev, [slideIndex]: true }));
    setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('ph_auth_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setSlideUploadProgress(prev => ({ ...prev, [slideIndex]: pct }));
      }
    };

    xhr.onload = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          const finalImageValue = mediaUrl || (mediaId ? String(mediaId) : '');

          if (finalImageValue) {
            setEditingItem(prev => {
              const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
              curImages[slideIndex] = finalImageValue;
              return {
                ...prev,
                images: curImages,
                image: slideIndex === 0 ? finalImageValue : (prev.image || finalImageValue)
              };
            });

            if (mediaUrl && mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: mediaUrl,
                [String(mediaId)]: mediaUrl
              }));
            }
            toast.success(`Foto Slide ${slideIndex + 1} berhasil diunggah`);
          } else {
            toast.error('Gagal membaca ID media');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setSlideUploading(prev => ({ ...prev, [slideIndex]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleRemoveSlideImage = (slideIndex) => {
    setEditingItem(prev => {
      const curImages = Array.isArray(prev.images) ? [...prev.images] : ['', '', ''];
      curImages[slideIndex] = '';
      const firstAvailable = curImages.find(Boolean) || '';
      return {
        ...prev,
        images: curImages,
        image: slideIndex === 0 ? firstAvailable : prev.image
      };
    });
    toast.info(`Foto Slide ${slideIndex + 1} dihapus`);
  };

  // Add bullet point highlight in modal
  const handleAddHighlight = () => {
    if (!newHighlightText.trim()) return;
    setEditingItem(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), newHighlightText.trim()]
    }));
    setNewHighlightText('');
    toast.success('Poin sorotan ditambahkan');
  };

  const handleRemoveHighlight = (hIdx) => {
    setEditingItem(prev => ({
      ...prev,
      highlights: (prev.highlights || []).filter((_, idx) => idx !== hIdx)
    }));
  };

  // Add structured Key Pillar / Initiative feature in modal
  const handleAddFeature = () => {
    if (!newFeatureTitle.trim()) {
      toast.warning('Silakan isi judul pilar / inisiatif terlebih dahulu');
      return;
    }
    const newFeature = {
      icon: newFeatureIcon || 'fa-handshake',
      title: newFeatureTitle.trim(),
      detail: (newFeatureDetail || '').trim()
    };
    setEditingItem(prev => ({
      ...prev,
      features: [...(prev.features || []), newFeature]
    }));
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    toast.success(`Pilar "${newFeature.title}" berhasil dimasukkan ke daftar`);
  };

  const handleEditFeature = (featIdx) => {
    const feat = (editingItem.features || [])[featIdx];
    if (!feat) return;
    setNewFeatureIcon(feat.icon || 'fa-handshake');
    setNewFeatureTitle(feat.title || '');
    setNewFeatureDetail(feat.detail || '');
    setEditingItem(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== featIdx)
    }));
    toast.info('Pilar dimuat kembali ke formulir untuk disunting');
  };

  const handleRemoveFeature = (featIdx) => {
    setEditingItem(prev => ({
      ...prev,
      features: (prev.features || []).filter((_, idx) => idx !== featIdx)
    }));
    toast.info('Pilar dihapus dari daftar');
  };

  // Save changes from modal into formData state
  const handleSaveModalChanges = () => {
    if (!editingItem?.title?.trim()) {
      toast.warning('Silakan isi judul press release');
      return false;
    }

    const cleanedImages = (Array.isArray(editingItem.images) 
      ? editingItem.images 
      : (editingItem.image ? [editingItem.image] : [])
    ).filter(Boolean).map(img => {
      const resolved = resolveImage(img) || (mediaMap && (mediaMap[img] || mediaMap[String(img)])) || img;
      if (typeof resolved === 'string' && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
        return resolved;
      }
      if (typeof resolved === 'string' && resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
        return `${origin}${resolved}`;
      }
      return resolved;
    });

    if (cleanedImages.length === 0 && !editingItem?.image) {
      toast.warning('Silakan upload minimal foto sampul (Slide 1) terlebih dahulu');
      return false;
    }

    if (!editingItem?.description?.trim()) {
      toast.warning('Silakan isi ringkasan singkat (excerpt) press release');
      return false;
    }

    if (!editingItem?.content?.trim()) {
      toast.warning('Silakan isi rilis pers lengkap');
      return false;
    }

    // Auto-commit pending highlight if user typed but did not press 'Tambah Poin'
    const finalHighlights = [...(editingItem.highlights || [])];
    if (newHighlightText && newHighlightText.trim()) {
      finalHighlights.push(newHighlightText.trim());
    }

    // Auto-commit pending feature pillar if user typed but did not press 'Tambahkan ke Daftar'
    const finalFeatures = [...(editingItem.features || [])];
    if (newFeatureTitle && newFeatureTitle.trim()) {
      finalFeatures.push({
        icon: newFeatureIcon || 'fa-handshake',
        title: newFeatureTitle.trim(),
        detail: (newFeatureDetail || '').trim()
      });
    }

    const slug = editingItem.id || editingItem.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const updatedItem = {
      ...editingItem,
      id: slug,
      title: editingItem.title.trim(),
      subtitle: editingItem.subtitle?.trim() || '',
      category: 'Press Release',
      author: editingItem.author?.trim() || 'Administration',
      date: editingItem.date?.trim() || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      description: editingItem.description?.trim() || '',
      content: editingItem.content?.trim() || '',
      highlights: finalHighlights.filter(Boolean),
      features: finalFeatures.filter(f => f && (f.title || f.detail)),
      image: cleanedImages[0] || editingItem.image || '',
      images: cleanedImages,
      link: `news-press-${slug}`
    };

    let targetIdx = editingIndex;
    if (editingIndex === null) {
      // Prepend new item
      setFormData(prev => ({
        ...prev,
        items: [updatedItem, ...prev.items]
      }));
      targetIdx = 0;
      toast.success(`Press Release "${updatedItem.title}" berhasil ditambahkan ke daftar`);
    } else {
      setFormData(prev => {
        const next = [...prev.items];
        next[editingIndex] = updatedItem;
        return { ...prev, items: next };
      });
      toast.success(`Perubahan press release "${updatedItem.title}" tersimpan di draf`);
    }

    setNewHighlightText('');
    setNewFeatureTitle('');
    setNewFeatureDetail('');
    setEditorModalOpen(false);
    return { item: updatedItem, index: targetIdx };
  };

  // Save and directly submit to approval workflow
  const handleSaveAndSubmitModal = async () => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const saved = handleSaveModalChanges();
    if (!saved) return;

    const { item, index } = saved;
    const isNew = editingIndex === null;
    const key = `item_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${isNew ? 'add' : 'update'}`,
          field_name: isNew ? `Add Press Release: ${item.title}` : `Press Release: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: item,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Press Release "${item.title}" berhasil diajukan untuk approval Kepala Sekolah!`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          if (isNew) {
            next.unshift(item);
          } else {
            next[index] = item;
          }
          return { ...prev, items: next };
        });

        // Clear local cache to ensure fresh fetch
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsPressForm] Submit modal error:', err);
      toast.error('Gagal mengajukan press release: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Quick photo upload from card
  const handleCardImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, and WebP images are supported');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 0 }));

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token') || 
                  localStorage.getItem('ph_auth_token') || 
                  localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          const mediaId = data.media_id || data.id || res.media_id || res.id;
          let mediaUrl = data.url || data.file_path || data.path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          const finalVal = mediaUrl || (mediaId ? String(mediaId) : '');

          if (finalVal) {
            handleItemChange(index, 'image', finalVal);
            setFormData(prev => {
              const next = [...prev.items];
              const curImgs = Array.isArray(next[index]?.images) ? [...next[index].images] : ['', '', ''];
              curImgs[0] = finalVal;
              next[index] = { ...next[index], image: finalVal, images: curImgs };
              return { ...prev, items: next };
            });
            setBrokenImages(prev => ({ ...prev, [index]: false }));
            if (mediaUrl && mediaId) {
              setMediaMap(prev => ({
                ...prev,
                [mediaId]: mediaUrl,
                [String(mediaId)]: mediaUrl
              }));
            }
            toast.success('Foto press release berhasil diupload');
          } else {
            toast.error('Gagal membaca media ID dari server');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Upload gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (field) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    if (formData[field] === originalData[field]) {
      toast.warning('Tidak ada perubahan pada field ini');
      return;
    }

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: field.charAt(0).toUpperCase() + field.slice(1),
          old_value: originalData[field],
          new_value: formData[field],
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${field}" berhasil diajukan untuk approval Kepala Sekolah`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: formData[field] }));
      } else {
        toast.error(result.error || result.message || 'Pengajuan gagal');
      }
    } catch (err) {
      console.error('[NewsPressForm] Submit error:', err);
      toast.error('Error saat submit: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  // Submit existing card from list
  const handleSubmitItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID not found');
      return;
    }

    const item = formData.items[index];
    if (!item?.title) {
      toast.warning('Silakan isi judul press release');
      return;
    }
    if (!item?.image || brokenImages[index]) {
      toast.warning('Silakan upload foto press release terlebih dahulu sebelum submit');
      return;
    }

    const isNew = !originalData.items?.[index] || !originalData.items[index]?.id;
    const op = isNew ? 'add' : 'update';
    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    const slug = item.id || item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    const autoLink = item.link || `news-press-${slug}`;
    const payloadItem = {
      ...item,
      id: slug,
      link: autoLink
    };

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.${op}`,
          field_name: isNew ? `Add Press Release: ${item.title}` : `Press Release: ${item.title}`,
          old_value: isNew ? null : (originalData.items?.[index] || {}),
          new_value: payloadItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(isNew 
          ? `Pengajuan press release baru "${item.title}" berhasil disubmit untuk approval`
          : `Press Release "${item.title}" berhasil disubmit untuk approval`);
        setPendingItems(prev => ({ ...prev, [key]: true }));
        setOriginalData(prev => {
          const next = [...(prev.items || [])];
          next[index] = payloadItem;
          return { ...prev, items: next };
        });
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[NewsPressForm] Item submit error:', err);
      toast.error('Error submitting press release: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // Remove press release
  const handleRemoveItem = async (index) => {
    if (formData.items.length <= 1) {
      toast.warning('Setidaknya harus ada satu press release');
      return;
    }

    const item = formData.items[index];
    const origIndex = originalData.items?.findIndex(it => 
      (it.id && item.id && String(it.id) === String(item.id)) ||
      (it.title && item.title && it.title.trim().toLowerCase() === item.title.trim().toLowerCase())
    );
    const isExistingInDb = origIndex !== -1 && origIndex !== undefined;
    const targetIndex = isExistingInDb ? origIndex : index;

    if (!isExistingInDb || !sectionId) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
      return;
    }

    const confirmDelete = window.confirm(`Apakah Anda yakin ingin menghapus "${item.title}"? Penghapusan akan diajukan ke Kepala Sekolah.`);
    if (!confirmDelete) return;

    const key = `item_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${targetIndex}.delete`,
          field_name: `Delete Press Release: ${item.title}`,
          old_value: originalData.items[targetIndex],
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus "${item.title}" berhasil diajukan`);
        setPendingItems(prev => ({ 
          ...prev, 
          [key]: true,
          [`item_${targetIndex}`]: true,
          [`item_${targetIndex}_delete`]: true 
        }));
        try {
          sessionStorage.removeItem('cms_page_news-updates');
          sessionStorage.removeItem('cms_page_news-and-updates');
          sessionStorage.removeItem('cms_sections_news-updates');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Gagal mengajukan penghapusan');
      }
    } catch (err) {
      console.error('[NewsPressForm] Delete submit error:', err);
      toast.error('Error saat submit hapus: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Press Releases content...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <img 
              src={lightboxImage} 
              alt="Preview" 
              style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }} 
            />
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              <X size={20} /> Close
            </button>
          </div>
        </div>
      )}

      {/* Comprehensive Press Release Editor Modal (Portal to body) */}
      {editorModalOpen && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setEditorModalOpen(false)}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div 
              style={{ 
                padding: '18px 24px', 
                borderBottom: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: '#f8fafc',
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: '#ede9fe', color: '#7c3aed', padding: '8px', borderRadius: '10px', display: 'flex' }}>
                  <Megaphone size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                    {editingIndex === null ? 'Tambah Press Release Baru' : `Edit Detail Press Release: ${editingItem.title || 'Press Release'}`}
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                    Lengkapi judul, topik resmi, ringkasan kartu, serta isi rilis pers lengkap yang tampil di modal popup dan halaman detail.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditorModalOpen(false)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body / Form Fields */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Row 1: Judul & Subjudul */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    Judul Press Release *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.title}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g. Strategic Partnerships or Community Outreach"
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    Subjudul / Topik Resmi (Subtitle / Topic)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.subtitle || ''}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="e.g. Kolaborasi Pendidikan Global & Pengabdian Masyarakat"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Row 2: Author & Tanggal */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <User size={14} color="#7c3aed" /> Penerbit Resmi / Narahubung (Author / Issued By)
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.author || ''}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="e.g. Administration, Communications Team, atau Office of the Principal"
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                    <Calendar size={14} color="#7c3aed" /> Tanggal Publikasi / Pernyataan *
                  </label>
                  <input
                    type="text"
                    className="unified-input"
                    value={editingItem.date || ''}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, date: e.target.value }))}
                    placeholder="e.g. October 2026 atau 15 October 2026"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* Row 3: Header Images Carousel (Up to 3 Slides) */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    <ImageIcon size={15} color="#7c3aed" /> Foto Header Slideshow (Maksimal 3 Foto)
                  </label>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    Slide 1: Cover Utama (Wajib) • Slide 2 & 3: Header Carousel (Opsional)
                  </span>
                </div>
                <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: '#64748b' }}>
                  Foto-foto ini akan membentuk slideshow otomatis pada header halaman detail rilis pers. Jika hanya 1 foto diunggah, foto tersebut akan tampil sebagai foto cover tunggal.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                  {[0, 1, 2].map((slideIdx) => {
                    const slideImg = (editingItem.images && editingItem.images[slideIdx]) || (slideIdx === 0 ? editingItem.image : '');
                    const isSlideUploading = slideUploading[slideIdx];
                    const slideProgress = slideUploadProgress[slideIdx] || 0;
                    const previewUrl = resolveImage(slideImg);
                    const isRequired = slideIdx === 0;

                    return (
                      <div 
                        key={slideIdx}
                        style={{
                          background: '#ffffff',
                          border: slideImg ? '1px solid #cbd5e1' : (isRequired ? '1px dashed #c084fc' : '1px dashed #cbd5e1'),
                          borderRadius: '10px',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        {/* Slide Title Bar */}
                        <div style={{ 
                          padding: '6px 10px', 
                          background: isRequired ? '#f5f3ff' : '#f8fafc', 
                          borderBottom: '1px solid #e2e8f0', 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center' 
                        }}>
                          <span style={{ fontSize: '11.5px', fontWeight: 600, color: isRequired ? '#6d28d9' : '#475569' }}>
                            {slideIdx === 0 ? 'Slide 1: Cover Utama *' : `Slide ${slideIdx + 1}: Header Carousel`}
                          </span>
                          {!isRequired && !slideImg && (
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>Opsional</span>
                          )}
                          {slideImg && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSlideImage(slideIdx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                              title="Hapus Foto Slide"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>

                        {/* Image Preview / Dropzone */}
                        <div style={{ height: '110px', position: 'relative', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {previewUrl ? (
                            <>
                              <img 
                                src={previewUrl} 
                                alt={`Slide ${slideIdx + 1}`} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                onClick={() => setLightboxImage(previewUrl)}
                                style={{
                                  position: 'absolute',
                                  bottom: '6px',
                                  right: '6px',
                                  background: 'rgba(0,0,0,0.65)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  color: '#fff',
                                  padding: '3px 6px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                                title="Lihat Foto"
                              >
                                <Eye size={12} />
                              </button>
                            </>
                          ) : (
                            <label
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: isRequired ? '#faf5ff' : '#ffffff',
                                cursor: isSlideUploading ? 'not-allowed' : 'pointer',
                                gap: '4px',
                                padding: '10px',
                                textAlign: 'center'
                              }}
                            >
                              <Upload size={18} color="#94a3b8" />
                              <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                                {isSlideUploading ? `Uploading ${slideProgress}%` : `Pilih Foto Slide ${slideIdx + 1}`}
                              </span>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => handleModalSlideImageUpload(slideIdx, e)}
                                disabled={isSlideUploading}
                                style={{ display: 'none' }}
                              />
                            </label>
                          )}

                          {isSlideUploading && (
                            <div style={{ 
                              position: 'absolute', 
                              inset: 0, 
                              background: 'rgba(15, 23, 42, 0.85)', 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              color: '#ffffff'
                            }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>
                                Uploading {slideProgress}%
                              </span>
                              <div style={{ width: '80%', height: '4px', background: '#334155', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ width: `${slideProgress}%`, height: '100%', background: '#7c3aed', transition: 'width 0.2s ease' }} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Re-upload button if preview exists */}
                        {previewUrl && !isSlideUploading && (
                          <div style={{ padding: '6px 8px', background: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
                            <label 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '4px', 
                                fontSize: '11px', 
                                color: '#6d28d9', 
                                fontWeight: 600, 
                                cursor: 'pointer' 
                              }}
                            >
                              <Upload size={11} /> Ganti Foto
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={(e) => handleModalSlideImageUpload(slideIdx, e)}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Row 4: Ringkasan Singkat / Lead Excerpt */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                  Ringkasan Singkat / Lead Excerpt (Tampil di Card & Modal Popup) *
                </label>
                <textarea
                  className="unified-textarea"
                  rows={2}
                  value={editingItem.description}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ringkasan 2-3 kalimat mengenai poin utama rilis pers ini..."
                  style={{ width: '100%', lineHeight: '1.5' }}
                />
              </div>

              {/* Row 5: Isi Rilis Pers Lengkap */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                  <FileText size={14} color="#7c3aed" /> Isi Rilis Pers Lengkap (Tampil di Modal Popup & Halaman Detail) *
                </label>
                <textarea
                  className="unified-textarea"
                  rows={6}
                  value={editingItem.content}
                  onChange={(e) => setEditingItem(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Tuliskan pernyataan resmi rilis pers secara lengkap, kutipan pimpinan, rincian program, dan dampak bagi komunitas..."
                  style={{ width: '100%', lineHeight: '1.6' }}
                />
              </div>

              {/* Row 6: Poin-Poin Sorotan / Key Milestones (Highlights) */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    <CheckCircle2 size={15} color="#7c3aed" /> Poin-Poin Sorotan / Key Milestones (Tampil di Halaman Detail)
                  </label>
                  <span style={{ fontSize: '11px', background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                    Opsional
                  </span>
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                  Tambahkan sorotan utama atau data penting terkait kegiatan ini (misal: "Kerjasama dengan 5 universitas terkemuka", "Diikuti 250 peserta", dll).
                </p>

                {/* List of existing highlights */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                  {(editingItem.highlights || []).map((hl, hIdx) => (
                    <div 
                      key={hIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '8px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        padding: '6px 12px',
                        borderRadius: '6px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <CheckCircle2 size={13} color="#7c3aed" />
                        <span style={{ fontSize: '13px', color: '#334155' }}>{hl}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveHighlight(hIdx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '3px'
                        }}
                        title="Hapus Poin"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {(editingItem.highlights || []).length === 0 && (
                    <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8', fontStyle: 'italic' }}>
                      Belum ada poin sorotan yang ditambahkan.
                    </p>
                  )}
                </div>

                {/* Add highlight input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newHighlightText}
                    onChange={(e) => setNewHighlightText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddHighlight();
                      }
                    }}
                    placeholder="Tulis poin sorotan baru..."
                    style={{ flex: 1, fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={handleAddHighlight}
                    style={{ whiteSpace: 'nowrap', padding: '6px 14px', fontSize: '12.5px' }}
                  >
                    <ListPlus size={14} /> Tambah Poin
                  </button>
                </div>
              </div>

              {/* Row 7: Key Pillars & Initiatives (Features - Optional) */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                    <Layers size={15} color="#7c3aed" /> Pilar Utama & Inisiatif Kerjasama (Key Pillars Section)
                  </label>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {(editingItem.features || []).length > 0 && (
                      <span style={{ fontSize: '11px', background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: '10px', fontWeight: 700 }}>
                        {(editingItem.features || []).length} Pilar Tersimpan
                      </span>
                    )}
                    <span style={{ fontSize: '11px', background: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                      Opsional
                    </span>
                  </div>
                </div>
                <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b' }}>
                  Bagian ini menampilkan kartu-kartu pilar (Key Pillars) di halaman detail rilis pers. Jika rilis pers Anda tidak membutuhkan poin pilar ini, Anda dapat mengosongkannya dan bagian ini tidak akan ditampilkan di halaman detail.
                </p>

                {/* List of existing features */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                  {(editingItem.features || []).map((feat, fIdx) => (
                    <div 
                      key={fIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '12px',
                        background: '#ffffff',
                        border: '1.5px solid #ddd6fe',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(124,58,237,0.06)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flex: 1 }}>
                        <div style={{ background: '#ede9fe', color: '#7c3aed', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`fas ${feat.icon || 'fa-handshake'}`} style={{ fontSize: '14px' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h5 style={{ margin: '0 0 2px 0', fontSize: '13.5px', fontWeight: 700, color: '#0f172a' }}>
                            {feat.title}
                          </h5>
                          <p style={{ margin: 0, fontSize: '12.5px', color: '#475569', lineHeight: '1.4' }}>
                            {feat.detail}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => handleEditFeature(fIdx)}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            color: '#334155',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                          title="Edit Pilar"
                        >
                          <Edit3 size={12} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFeature(fIdx)}
                          style={{
                            background: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            color: '#ef4444',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '11.5px',
                            fontWeight: 600
                          }}
                          title="Hapus Pilar"
                        >
                          <Trash2 size={12} /> Hapus
                        </button>
                      </div>
                    </div>
                  ))}
                  {(editingItem.features || []).length === 0 && (
                    <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                      <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b', fontStyle: 'italic' }}>
                        Belum ada pilar yang tersimpan di daftar rilis pers. Gunakan formulir di bawah ini untuk menambahkan pilar baru.
                      </p>
                    </div>
                  )}
                </div>

                {/* Add new feature pillar sub-form */}
                <div style={{ background: '#ffffff', border: '1.5px solid #c084fc', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 2px 6px rgba(124,58,237,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#581c87', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Plus size={15} color="#7c3aed" /> Form Tambah / Edit Pilar Kerjasama:
                    </span>
                    {newFeatureTitle.trim() && (
                      <span style={{ fontSize: '11px', background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                        Siap disimpan
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Pilih Ikon</label>
                      <select
                        className="unified-input"
                        value={newFeatureIcon}
                        onChange={(e) => setNewFeatureIcon(e.target.value)}
                        style={{ fontSize: '12px', padding: '7px 8px' }}
                      >
                        {FEATURE_ICON_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Judul Pilar / Kerjasama *</label>
                      <input
                        type="text"
                        className="unified-input"
                        value={newFeatureTitle}
                        onChange={(e) => setNewFeatureTitle(e.target.value)}
                        placeholder="e.g. University Partnerships, CSR Outreach"
                        style={{ fontSize: '12px', padding: '7px 8px' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '11.5px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '3px' }}>Penjelasan Detail Pilar</label>
                    <textarea
                      className="unified-textarea"
                      rows={2}
                      value={newFeatureDetail}
                      onChange={(e) => setNewFeatureDetail(e.target.value)}
                      placeholder="Penjelasan ringkas 1-2 kalimat mengenai poin atau kemitraan ini..."
                      style={{ fontSize: '12px', padding: '7px 8px' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                      {newFeatureTitle.trim() 
                        ? 'Pilar ini otomatis tersimpan saat Anda klik Simpan, atau klik tombol ungu untuk menambah pilar lain.' 
                        : 'Ketik Judul Pilar dan klik tombol ungu untuk memasukkan ke daftar.'}
                    </span>
                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={handleAddFeature}
                      style={{ padding: '7px 18px', fontSize: '12.5px', borderRadius: '8px', background: '#7c3aed', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', border: 'none', fontWeight: 600 }}
                    >
                      <Plus size={14} /> + Tambahkan Pilar ke Daftar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div 
              style={{ 
                padding: '16px 24px', 
                borderTop: '1px solid #e2e8f0', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: '#f8fafc'
              }}
            >
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => setEditorModalOpen(false)}
              >
                Batal
              </button>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={handleSaveModalChanges}
                  style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
                >
                  <CheckCircle2 size={14} /> Terapkan ke Daftar
                </button>

                <button
                  type="button"
                  className="unified-submit-button"
                  onClick={handleSaveAndSubmitModal}
                  style={{ background: '#7c3aed' }}
                >
                  <Save size={14} /> Simpan & Ajukan ke Approval
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon" style={{ background: '#ede9fe', color: '#7c3aed' }}>
          <Megaphone size={22} />
        </div>
        <div>
          <h2 className="unified-title">Press Releases & Partnerships</h2>
          <p className="unified-subtitle">
            Kelola rilis pers resmi, sorotan kegiatan outreach, dan pembaruan kolaborasi strategis sekolah.
          </p>
        </div>
      </div>

      {/* Field: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker || submitting.kicker}
            placeholder="e.g. Press Releases"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
            style={{ background: '#7c3aed' }}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title || submitting.title}
            placeholder="e.g. Community Involvement & Partnerships"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
            style={{ background: '#7c3aed' }}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={pendingFields.description || submitting.description}
            placeholder="Deskripsi pengantar rilis pers dan kemitraan..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px', background: '#7c3aed' }}
            onClick={() => handleSubmitField('description')}
            disabled={pendingFields.description || submitting.description || formData.description === originalData.description}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Press Release Cards Grid Header */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Daftar Press Releases ({formData.items.length})</label>
            <p className="unified-field-desc">
              Kelola rilis pers individual. Pengunjung dapat membaca ringkasan pada popup modal dan membuka halaman detail penuh.
            </p>
          </div>
          <button
            type="button"
            className="unified-submit-button"
            onClick={handleOpenAddModal}
            style={{ background: '#7c3aed', padding: '8px 18px', fontSize: '13px' }}
          >
            <Plus size={15} /> + Tambah Press Release Baru
          </button>
        </div>

        {/* Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '22px', marginTop: '18px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[`item_${idx}`];
            const isPendingDelete = pendingItems[`item_${idx}_delete`];
            const isSubmitting = submitting[`item_${idx}`];
            const isUploading = imageUploading[idx];
            const previewUrl = resolveImage(item.image, idx);
            const isSubmitDisabled = isPending || isPendingDelete || isSubmitting || !item.title || !item.image || brokenImages[idx];

            return (
              <div 
                key={item.id || idx} 
                style={{ 
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
                  borderRadius: '14px', 
                  background: isPending ? '#fffdfa' : '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Dropzone / Preview */}
                <div 
                  style={{ 
                    height: '160px', 
                    background: '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                >
                  {previewUrl ? (
                    <>
                      <img 
                        src={previewUrl} 
                        alt={item.title || `Press Release ${idx + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setLightboxImage(previewUrl)}
                        onError={() => setBrokenImages(prev => ({ ...prev, [idx]: true }))}
                      />
                      <button
                        type="button"
                        onClick={() => setLightboxImage(previewUrl)}
                        style={{
                          position: 'absolute',
                          bottom: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.65)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          padding: '4px 8px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Lihat Foto"
                      >
                        <Eye size={13} />
                      </button>
                    </>
                  ) : (
                    <label 
                      style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isPending || isUploading ? 'not-allowed' : 'pointer',
                        gap: '6px',
                        padding: '16px',
                        textAlign: 'center'
                      }}
                    >
                      <Upload size={22} color="#94a3b8" />
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>
                        {brokenImages[idx] ? 'Foto Rusak — Klik untuk Ganti' : 'Klik untuk Upload Cover Foto'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG, atau WebP (Maks 5MB)</span>
                      <input 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp" 
                        style={{ display: 'none' }} 
                        disabled={isPending || isUploading}
                        onChange={(e) => handleCardImageUpload(idx, e)}
                      />
                    </label>
                  )}

                  {/* Upload Progress Overlay */}
                  {isUploading && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(15, 23, 42, 0.85)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        color: '#fff',
                        zIndex: 10
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                        Uploading: {uploadProgress[idx] || 0}%
                      </div>
                      <div style={{ width: '80%', height: '8px', background: '#334155', borderRadius: '4px', overflow: 'hidden' }}>
                        <div 
                          style={{ 
                            width: `${uploadProgress[idx] || 0}%`, 
                            height: '100%', 
                            background: '#7c3aed', 
                            transition: 'width 0.2s ease' 
                          }} 
                        />
                      </div>
                    </div>
                  )}

                  {/* Change photo button if preview exists */}
                  {previewUrl && !isUploading && (
                    <label 
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'rgba(0,0,0,0.65)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        padding: '4px 8px',
                        cursor: isPending ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Ganti Foto"
                    >
                      <Upload size={12} /> Ganti Foto
                      <input 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp" 
                        style={{ display: 'none' }} 
                        disabled={isPending}
                        onChange={(e) => handleCardImageUpload(idx, e)}
                      />
                    </label>
                  )}
                </div>

                {/* Card Header & Controls */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ 
                        background: '#f5f3ff', 
                        color: '#6d28d9', 
                        fontSize: '11px', 
                        fontWeight: 700, 
                        padding: '2px 8px', 
                        borderRadius: '12px' 
                      }}>
                        PRESS #{idx + 1}
                      </span>
                      {isPendingDelete ? (
                        <span className="unified-pending-pill" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                          <Clock size={11} /> Pending Delete
                        </span>
                      ) : isPending ? (
                        <span className="unified-pending-pill">
                          <Clock size={11} /> Review
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(idx)}
                      disabled={isPending || isPendingDelete}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: isPending || isPendingDelete ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '4px',
                        opacity: isPending || isPendingDelete ? 0.4 : 1
                      }}
                      title="Hapus Press Release"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Title & Date display */}
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                      {item.title || '(Belum ada judul)'}
                    </h4>
                    {item.subtitle && (
                      <p style={{ margin: '0 0 6px 0', fontSize: '12.5px', color: '#6d28d9', fontWeight: 500 }}>
                        {item.subtitle}
                      </p>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11.5px', color: '#64748b' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Calendar size={12} /> {item.date || 'October 2026'}
                      </span>
                      <span>•</span>
                      <span>{item.author || 'Administration'}</span>
                    </div>
                  </div>

                  {/* Summary / Excerpt preview */}
                  <p style={{ margin: 0, fontSize: '12.5px', color: '#475569', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.description || 'Belum ada ringkasan rilis pers.'}
                  </p>

                  {/* Feature badges if available */}
                  {(item.features || []).length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {(item.features || []).slice(0, 2).map((f, fIdx) => (
                        <span key={fIdx} style={{ fontSize: '11px', background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <i className={`fas ${f.icon || 'fa-handshake'}`} style={{ fontSize: '10px', color: '#7c3aed' }} /> {f.title}
                        </span>
                      ))}
                      {(item.features || []).length > 2 && (
                        <span style={{ fontSize: '10px', color: '#94a3b8' }}>+{(item.features || []).length - 2} lagi</span>
                      )}
                    </div>
                  )}

                  {/* Card Action Buttons: Edit Detail Modal & Submit */}
                  <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditModal(idx)}
                      disabled={isPending}
                      style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                      <Edit3 size={13} /> Edit Detail
                    </button>

                    <button
                      type="button"
                      className="unified-submit-button"
                      onClick={() => handleSubmitItem(idx)}
                      disabled={isSubmitDisabled}
                      style={{
                        padding: '6px 14px',
                        fontSize: '12px',
                        background: '#7c3aed',
                        opacity: isSubmitDisabled ? 0.6 : 1,
                        cursor: isSubmitDisabled ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Save size={13} /> {isSubmitting ? 'Saving...' : isPending ? 'Pending' : 'Submit Card'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
